import { useEffect, useState } from "react";
import { Link, useNavigate } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphsApi, type AgentGraph, type GraphDefinition } from "../api/graphs";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { queryKeys } from "../lib/queryKeys";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/PageSkeleton";
import { Bot, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { timeAgo } from "../lib/timeAgo";
import { cn } from "../lib/utils";
import { TemplateGallery } from "../components/studio/TemplateGallery";

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  archived: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
};

function GraphCard({ graph, onDelete }: { graph: AgentGraph; onDelete: (id: string) => void }) {
  const nodeCount = graph.graphDefinition?.nodes?.length ?? 0;
  const edgeCount = graph.graphDefinition?.edges?.length ?? 0;

  return (
    <div className="group relative rounded-xl border border-border bg-card shadow-sm transition-all hover:shadow-md hover:border-primary/30">
      <Link
        to={`/studio/${graph.id}`}
        className="block p-5 no-underline text-inherit"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bot className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                {graph.name}
              </h3>
              {graph.description && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {graph.description}
                </p>
              )}
            </div>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
              STATUS_STYLES[graph.status] ?? STATUS_STYLES.draft,
            )}
          >
            {graph.status}
          </span>
        </div>

        <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
          <span>{nodeCount} nodes</span>
          <span>{edgeCount} edges</span>
          <span className="ml-auto">{timeAgo(graph.updatedAt)}</span>
        </div>
      </Link>
      {/* Delete button — outside Link to avoid navigation conflict */}
      <button
        type="button"
        className="absolute top-3 right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
        title="Delete agent"
        onClick={() => onDelete(graph.id)}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function AgentStudio() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showTemplates, setShowTemplates] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Agent Studio" }]);
  }, [setBreadcrumbs]);

  const { data: graphs, isLoading } = useQuery({
    queryKey: queryKeys.graphs.list(selectedPracticeId!),
    queryFn: () => graphsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const createMutation = useMutation({
    mutationFn: (args: { name: string; graphDefinition: GraphDefinition }) =>
      graphsApi.create(selectedPracticeId!, args),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.graphs.list(selectedPracticeId!),
      });
      navigate(`/studio/${created.id}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => graphsApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.graphs.list(selectedPracticeId!),
      });
      setDeleteId(null);
    },
  });

  const handleTemplateSelect = (name: string, definition: GraphDefinition) => {
    setShowTemplates(false);
    createMutation.mutate({ name, graphDefinition: definition });
  };

  if (!selectedPracticeId) {
    return <EmptyState icon={Bot} message="Select a practice." />;
  }

  if (isLoading) {
    return <PageSkeleton variant="list" />;
  }

  // Filter to only show Agent Studio graphs (those with "start" nodes, not workflow wf_start)
  const agentGraphs = (graphs ?? []).filter((g) => {
    const nodes = g.graphDefinition?.nodes;
    if (!nodes || nodes.length === 0) return true;
    return nodes.some((n) => n.type === "start") && !nodes.some((n) => n.type === "wf_start");
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">AI Agent Studio</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build, configure, and deploy AI agents
          </p>
        </div>
        <Button onClick={() => setShowTemplates(true)} disabled={createMutation.isPending}>
          <Plus className="h-4 w-4 mr-1.5" />
          New Agent
        </Button>
      </div>

      {agentGraphs.length === 0 ? (
        <EmptyState
          icon={Bot}
          message="No agents yet. Create your first AI agent to get started."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agentGraphs.map((graph) => (
            <GraphCard key={graph.id} graph={graph} onDelete={(id) => setDeleteId(id)} />
          ))}
        </div>
      )}

      {showTemplates && (
        <TemplateGallery onSelect={handleTemplateSelect} onClose={() => setShowTemplates(false)} />
      )}

      {/* Delete confirmation modal — same pattern as Workflow Studio */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card shadow-xl mx-4 p-6">
            <h3 className="text-sm font-semibold mb-2">Delete Agent?</h3>
            <p className="text-sm text-muted-foreground mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(deleteId)} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
